/**
 * Parse cantidad field — may be a number, numeric string, fraction, or full phrase ("3 litros").
 */
export function parseCantidadField(raw) {
  if (raw == null || raw === '') {
    return { numeric: 0, text: '', isText: false };
  }

  if (typeof raw === 'number' && !Number.isNaN(raw)) {
    return { numeric: raw, text: '', isText: false };
  }

  const str = String(raw).trim();
  if (!str) {
    return { numeric: 0, text: '', isText: false };
  }

  if (/^-?\d+([.,]\d+)?$/.test(str)) {
    return { numeric: parseFloat(str.replace(',', '.')), text: '', isText: false };
  }

  if (/^\d+\s*\/\s*\d+$/.test(str)) {
    return { numeric: 0, text: str, isText: true };
  }

  return { numeric: 0, text: str, isText: true };
}

function formatNumericAmount(num) {
  if (!num || Number.isNaN(num)) return '';
  if (num === Math.floor(num)) return String(Math.floor(num));
  return String(Math.round(num * 100) / 100).replace(/\.?0+$/, '');
}

/**
 * Display label for ingredient amount column (handles string cantidad from production DB).
 */
export function getIngredientAmountDisplay(ing) {
  if (!ing) return '—';

  const raw = ing.cantidad ?? ing.cantidad_total ?? ing.quantity;
  const unit = String(ing.unidad || ing.unit || '').trim();
  const parsed = parseCantidadField(raw);

  if (parsed.isText && parsed.text) {
    return parsed.text;
  }

  if (parsed.numeric > 0) {
    const qStr = formatNumericAmount(parsed.numeric);
    return unit ? `${qStr} ${unit}` : qStr;
  }

  if (unit) return unit;
  return 'al gusto';
}

/**
 * Numeric quantity for aggregation/math only.
 */
export function getIngredientNumericQuantity(ing) {
  if (!ing) return 0;
  const raw = ing.cantidad_total ?? ing.cantidad ?? ing.quantity;
  const parsed = parseCantidadField(raw);
  if (parsed.isText) {
    if (/^\d+\s*\/\s*\d+$/.test(parsed.text)) {
      const [a, b] = parsed.text.split('/').map((part) => parseFloat(part.trim()));
      return b ? a / b : 0;
    }
    return 0;
  }
  return parsed.numeric || 0;
}

/**
 * Normalize ingredient JSONB rows from production (ingrediente, nombre, name, or plain strings).
 */
export function parseIngredientes(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function normalizeIngredient(ing) {
  if (!ing) return null;

  if (typeof ing === 'string') {
    const name = ing.trim();
    if (!name) return null;
    return {
      ingrediente: name,
      unidad: '',
      cantidad: 0,
      por_persona: 0,
      cantidad_total: 0,
    };
  }

  if (typeof ing !== 'object') return null;

  const name =
    ing.ingrediente ||
    ing.nombre ||
    ing.name ||
    ing.ingredient ||
    ing.Ingrediente ||
    '';

  const trimmed = String(name).trim();
  if (!trimmed) return null;

  const rawCantidad = ing.cantidad ?? ing.cantidad_total ?? ing.quantity;
  const parsed = parseCantidadField(rawCantidad);
  const unidad = String(ing.unidad || ing.unit || '').trim();

  const cantidad = parsed.isText ? parsed.text : parsed.numeric;
  const cantidadTotalRaw = ing.cantidad_total ?? ing.cantidad ?? ing.quantity;
  const parsedTotal = parseCantidadField(cantidadTotalRaw);
  const cantidad_total = parsedTotal.isText ? parsedTotal.text : parsedTotal.numeric;

  return {
    ingrediente: trimmed,
    unidad: parsed.isText ? '' : unidad,
    cantidad,
    por_persona: Number(ing.por_persona ?? ing.per_person) || 0,
    cantidad_total,
  };
}

export function normalizeIngredientList(raw) {
  return parseIngredientes(raw).map(normalizeIngredient).filter(Boolean);
}
